import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, Switch, useRouteMatch, useLocation } from 'react-router-dom';
import DashboardLayout from '../../layouts/DashoardLayout';
import PrivateRoute from '../../components/shared/PrivateRoute';
import DriversTriage from './DriversTriage';
import ApprovalFunnel from './ApprovalFunnel';
import DriversSearch from '../../components/widgets/DriversApproval/DriversSearch';
import SearchBar from '../../components/shared/SearchBar';
import { Box, List, ListItemButton, Paper, Typography, IconButton } from '../../ui/core';
import { SearchIcon } from '../../ui/icons';
import { flushPageSx, surfaceCardSx, hairline, railItemSx } from '../../ui/styles';
import { DriverStatusOptions } from '../../models/Drivers';

// Secondary panel — the per-status sub-nav that used to live as sublinks under
// "Driver Approvals" in the classic drawer. Same [rail | content] shape as
// Promotions: a list card of statuses beside the triage/funnel content card.
const StatusRail = ({ searchActive, onToggleSearch }) => {
  // StatusRail is a sibling of the Switch, not a descendant of the Route that
  // defines `:driverApplicationStatus`, so useParams() here would always be
  // empty. Match the triage route directly to read the param instead.
  const triageMatch = useRouteMatch('/drivers-approval/drivers-triage/:driverApplicationStatus');
  const funnelMatch = useRouteMatch('/drivers-approval/funnel/:id');
  // On a driver's profile there's no status in the URL, so fall back to that
  // driver's own application status — the rail keeps highlighting the category
  // you're in even after drilling into a profile.
  const selectedDriverStatus = useSelector((state) => state.drivers.selectedDriver?.driverApplicationStatus);
  const activeStatus =
    triageMatch?.params?.driverApplicationStatus ?? (funnelMatch ? selectedDriverStatus : undefined);

  return (
    <Paper elevation={0} sx={{ ...surfaceCardSx, width: { xs: '100%', md: 240 }, flexShrink: 0 }}>
      <Box
        sx={{ p: '16px', pb: '12px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>Status</Typography>
        {/* Search toggle — opens a full-width search bar over the content area
            (see DriversApprovalShell), the way Intercom's Contacts panel does. */}
        <IconButton
          size="small"
          onClick={onToggleSearch}
          aria-label="Search drivers"
          sx={{ color: searchActive ? 'text.primary' : 'text.secondary' }}
        >
          <SearchIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, px: '8px', pb: '8px', overflowY: 'auto' }}>
        <List disablePadding>
          {DriverStatusOptions.map(({ label, value }) => (
            <ListItemButton
              key={value}
              component={NavLink}
              to={`/drivers-approval/drivers-triage/${value}?page=1`}
              selected={value === activeStatus}
              disableRipple
              sx={railItemSx}
            >
              {label}
            </ListItemButton>
          ))}
        </List>
      </Box>
    </Paper>
  );
};

// DriversTriage and ApprovalFunnel each own their own header row (title +
// filters/search), so the content card renders no header of its own — same
// pattern as Stations, where the search lives inside the table's header.
const DriversApprovalShell = () => {
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  // Dismiss the search bar on any navigation (e.g. clicking a result), so it
  // doesn't linger above — and shrink — the driver profile.
  useEffect(() => {
    setSearchOpen(false);
  }, [location.pathname]);

  return (
    <Box sx={flushPageSx}>
      <StatusRail searchActive={searchOpen} onToggleSearch={() => setSearchOpen((open) => !open)} />
      <Paper
        elevation={0}
        sx={{ ...surfaceCardSx, flex: 1, minWidth: 0, borderLeft: (theme) => `1px solid ${hairline(theme)}` }}
      >
        {/* Full-width search bar across the top of the content, matching
            Intercom's search-open state. */}
        {searchOpen && (
          <SearchBar onClose={() => setSearchOpen(false)}>
            <DriversSearch variant="bar" width="100%" autoFocus placeholder="Search drivers by name or email" />
          </SearchBar>
        )}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Switch>
            <PrivateRoute path="/drivers-approval/drivers-triage/:driverApplicationStatus" component={DriversTriage} />
            <PrivateRoute path="/drivers-approval/funnel/:id" component={ApprovalFunnel} />
            <PrivateRoute component={DriversTriage} />
          </Switch>
        </Box>
      </Paper>
    </Box>
  );
};

const DriversApproval = () => (
  <DashboardLayout flush title="Drivers">
    <DriversApprovalShell />
  </DashboardLayout>
);

export default DriversApproval;
